import { View, Text } from "react-native";
import type { DogStats } from "../app/lib/types";
import { formatTime } from "../app/lib/format";

interface StatsCardProps {
  stats: DogStats;
  /** Heading label. Defaults to "Performance Stats". Pass "2026" → "2026 Performance Stats". */
  yearLabel?: string;
  /**
   * Override for the Total Points value. When provided, it's rendered with an
   * "(all-time)" subtitle so a current-year card can still show lifetime points
   * for title-progress context. Leave undefined to render stats.totalPoints as-is.
   */
  totalPointsAllTime?: number;
  /**
   * Lifetime Career Performance block (Total Runs / Best Time / Top Speed)
   * rendered under Run 1 vs Run 2. Only pass for non-rookie dogs — caller is
   * responsible for skipping this when the dog has only run in the current year.
   */
  careerStats?: {
    totalRuns: number;
    personalBestTime: number;
    personalBestSpeed: number;
  };
}

function StatItem({ label, value, subtitle }: { label: string; value: string; subtitle?: string }) {
  return (
    <View style={{ alignItems: "center", flex: 1 }}>
      <Text style={{ fontSize: 20, fontWeight: "800", color: "#F5A623" }}>{value}</Text>
      <Text style={{ fontSize: 14, color: "#6b7280", marginTop: 3, textAlign: "center" }}>{label}</Text>
      {subtitle && (
        <Text style={{ fontSize: 10, color: "#9ca3af", marginTop: 1, textAlign: "center" }}>{subtitle}</Text>
      )}
    </View>
  );
}

export function StatsCard({ stats, yearLabel, totalPointsAllTime, careerStats }: StatsCardProps) {
  const heading = yearLabel ? `${yearLabel} Performance Stats` : "Performance Stats";
  const pointsValue = totalPointsAllTime != null ? totalPointsAllTime : stats.totalPoints;
  const pointsSubtitle = totalPointsAllTime != null ? "all-time" : undefined;
  return (
    <View
      style={{
        backgroundColor: "#ffffff",
        borderRadius: 14,
        padding: 20,
        borderWidth: 1,
        borderColor: "#e5e7eb",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
        elevation: 2,
        marginBottom: 16,
      }}
    >
      <Text style={{ fontSize: 16, fontWeight: "700", color: "#374151", marginBottom: 14 }}>
        {heading}
      </Text>
      {/* 2x2 grid */}
      <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 12 }}>
        <StatItem label="Total Runs" value={String(stats.totalRuns)} />
        <View style={{ width: 1, backgroundColor: "#f3f4f6" }} />
        <StatItem label="Best Time" value={stats.personalBestTime > 0 ? `${formatTime(stats.personalBestTime)}s` : "—"} />
      </View>
      <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
        <StatItem label="Top Speed" value={`${stats.personalBestSpeed.toFixed(2)} mph`} />
        <View style={{ width: 1, backgroundColor: "#f3f4f6" }} />
        <StatItem label="Total Points" value={pointsValue.toFixed(1)} subtitle={pointsSubtitle} />
      </View>
      {stats.bestThreeAvgSpeed > 0 && stats.totalRuns >= 3 && (
        <View style={{ marginTop: 12, backgroundColor: "#f0fdf4", borderRadius: 8, padding: 10, flexDirection: "row", justifyContent: "center", alignItems: "center", gap: 6 }}>
          <Text style={{ fontSize: 14, color: "#15803d" }}>Best 3 Avg:</Text>
          <Text style={{ fontSize: 14, fontWeight: "800", color: "#15803d" }}>{stats.bestThreeAvgSpeed.toFixed(2)} mph</Text>
          <Text style={{ fontSize: 13, color: "#16a34a" }}>(ranking speed)</Text>
        </View>
      )}
      {/* Run 1 vs Run 2 comparison */}
      {stats.run1Count > 0 && stats.run2Count > 0 && (
        <View style={{ marginTop: 12, backgroundColor: "#f5f3ff", borderRadius: 8, padding: 12 }}>
          <Text style={{ fontSize: 14, fontWeight: "700", color: "#6d28d9", marginBottom: 8, textAlign: "center" }}>
            Run 1 vs Run 2
          </Text>
          <View style={{ flexDirection: "row", justifyContent: "space-around" }}>
            <View style={{ alignItems: "center" }}>
              <Text style={{ fontSize: 16, fontWeight: "800", color: stats.run1AvgSpeed >= stats.run2AvgSpeed ? "#16a34a" : "#374151" }}>
                {stats.run1AvgSpeed.toFixed(2)}
              </Text>
              <Text style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>Run 1 avg ({stats.run1Count})</Text>
            </View>
            <Text style={{ fontSize: 16, color: "#d1d5db", alignSelf: "center" }}>vs</Text>
            <View style={{ alignItems: "center" }}>
              <Text style={{ fontSize: 16, fontWeight: "800", color: stats.run2AvgSpeed > stats.run1AvgSpeed ? "#16a34a" : "#374151" }}>
                {stats.run2AvgSpeed.toFixed(2)}
              </Text>
              <Text style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>Run 2 avg ({stats.run2Count})</Text>
            </View>
          </View>
        </View>
      )}
      {/* Career Performance — lifetime totals at-a-glance. Caller passes only
          for multi-year dogs; rookies get nothing here so the section feels
          earned, not redundant. */}
      {careerStats && (
        <View style={{ marginTop: 12, backgroundColor: "#fffbeb", borderRadius: 8, padding: 12 }}>
          <Text style={{ fontSize: 14, fontWeight: "700", color: "#92400e", marginBottom: 8, textAlign: "center" }}>
            Career Performance
          </Text>
          <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
            <StatItem label="Total Runs" value={String(careerStats.totalRuns)} />
            <View style={{ width: 1, backgroundColor: "#fde68a" }} />
            <StatItem label="Best Time" value={careerStats.personalBestTime > 0 ? `${formatTime(careerStats.personalBestTime)}s` : "—"} />
            <View style={{ width: 1, backgroundColor: "#fde68a" }} />
            <StatItem label="Top Speed" value={`${careerStats.personalBestSpeed.toFixed(2)} mph`} />
          </View>
        </View>
      )}
    </View>
  );
}
